import { get } from "@/utils/lodash";
import { getDefaultAvailability, getConsignmentInventory, getBoardInventory } from "@/utils/market-utils";

/**
 * Determines the availability tier for a product (lower = higher priority):
 *  0 = Available (inStock > 0)
 *  1 = Upcoming (has itemToCome but NOT in stock — note: products with both
 *      inStock > 0 AND itemToCome are treated as available, per Charles's spec)
 *  2 = Sold out with availability history (has productInventories entries)
 *  3 = Sold out, never had inventory (productInventories is empty/missing)
 */
function getAvailabilityTier(product) {
  // Check erpProduct first (enriched products), then top-level (carousel API products)
  const inStock =
    get(product, "erpProduct.quantity.inStock") ??
    get(product, "quantity.inStock", 0);
  if (inStock > 0) return 0;

  const itemToCome =
    get(product, "erpProduct.itemToCome.receptionPlanningDate") ||
    get(product, "itemToCome.receptionPlanningDate");
  if (itemToCome) return 1;

  // Products with productInventories or a top-level inventory were once listed
  const productInventories = get(product, "productInventories", []);
  const hasInventory = get(product, "inventory.code");
  if (productInventories.length > 0 || hasInventory) return 2;

  return 3;
}

/**
 * Gets the upcoming date as a timestamp for sorting (oldest first).
 */
function getUpcomingDate(product) {
  const date =
    get(product, "erpProduct.itemToCome.receptionPlanningDate") ||
    get(product, "itemToCome.receptionPlanningDate");
  return date ? new Date(date).getTime() : Infinity;
}

/**
 * Gets the vintage year as a number for sorting (oldest first).
 * Falls back to Infinity so products without a vintage sort last.
 */
function getVintage(product) {
  const vintage =
    get(product, "erpProduct.currentVintage") ||
    get(product, "currentVintage");
  const parsed = parseInt(vintage, 10);
  return isNaN(parsed) ? Infinity : parsed;
}

/**
 * Sorts products by the Boires carousel hierarchy:
 *  1. Available products (sorted by oldest vintage first)
 *  2. Upcoming products (sorted by soonest reception date first)
 *  3. Sold out with past availability (had productInventories)
 *  4. Sold out, never available (no productInventories)
 */
export function sortByHierarchy(products) {
  if (!products || products.length === 0) return [];

  return [...products].sort((a, b) => {
    const tierA = getAvailabilityTier(a);
    const tierB = getAvailabilityTier(b);

    if (tierA !== tierB) return tierA - tierB;

    // Within available tier: sort by oldest vintage first
    if (tierA === 0) {
      return getVintage(a) - getVintage(b);
    }

    // Within upcoming tier: sort by soonest reception date first
    if (tierA === 1) {
      return getUpcomingDate(a) - getUpcomingDate(b);
    }

    return 0;
  });
}

/**
 * Gets the inventory type group for a product in the current market.
 * Returns: "board" (SAQ/LCBO), "consignment" (IP/PI), or "direct" (Brouhh/MERCH/STORE/BERGEVILLE)
 */
function getInventoryGroup(product, market) {
  const code = get(product, "inventory.code", "").toUpperCase();
  const board = getBoardInventory(market).toUpperCase();
  const consignment = getConsignmentInventory(market).toUpperCase();

  if (code === board) return "board";
  if (code === consignment) return "consignment";
  if (code) return "direct";
  return "none";
}

/**
 * Sorts products by hierarchy then alternates between inventory types
 * (board, consignment, direct) for the available tier.
 *
 * Used on blog, producer, and product page carousels.
 */
export function sortByHierarchyWithAlternation(products, market) {
  if (!products || products.length === 0) return [];

  const sorted = sortByHierarchy(products);

  // Split available products by inventory group
  const available = sorted.filter((p) => getAvailabilityTier(p) === 0);
  const rest = sorted.filter((p) => getAvailabilityTier(p) !== 0);

  if (available.length === 0) return sorted;

  // Bucket available products by inventory group
  const buckets = { board: [], consignment: [], direct: [] };
  available.forEach((p) => {
    const group = getInventoryGroup(p, market);
    if (buckets[group]) {
      buckets[group].push(p);
    } else {
      buckets.direct.push(p);
    }
  });

  // Round-robin alternation across non-empty buckets
  const order = ["consignment", "board", "direct"];
  const alternated = [];
  let added = true;
  let round = 0;

  while (added) {
    added = false;
    for (const key of order) {
      if (round < buckets[key].length) {
        alternated.push(buckets[key][round]);
        added = true;
      }
    }
    round++;
  }

  return [...alternated, ...rest];
}

/**
 * Enriches products with erpProduct/inventory based on market availability codes.
 * This is a common pattern used across home, account, provider pages.
 */
export function enrichProductsForMarket(products, market) {
  if (!products) return [];
  const allowedCodes = getDefaultAvailability(market).map((c) => c.toUpperCase());

  products.forEach((e) => {
    if (e.productInventories) {
      const exactMatch = e.puid
        ? e.productInventories.find((r) => r && r.puid === e.puid)
        : null;
      const match = exactMatch || e.productInventories.find(
        (r) => r.inventory && allowedCodes.indexOf(r.inventory.code.toUpperCase()) !== -1
      );
      if (match) {
        e.erpProduct = match;
        e.inventory = match.inventory;
        e.pricing = match.pricing;
      } else {
        e.erpProduct = Object.assign({}, e, { quantity: { inStock: 0 }, itemToCome: null });
        e.inventory = null;
      }
    }
  });

  return products;
}
